import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ScribbleUnderline from './ScribbleUnderline';

const features = [
  { title: 'Email that sorts itself', description: 'Your assistant reads, categorises, drafts responses, and surfaces what actually needs your eyes. Within 48 hours, your inbox becomes a source of clarity, not anxiety.', icon: FiIcons.FiMail, tag: 'Day 1–2', accent: '#2A5C8A', accentLight: '#EBF2F9', accentBorder: '#C8DDE9', note: 'avg 47 emails/day handled' },
  { title: 'Scheduling without the back-and-forth', description: 'Meeting requests, reschedules, prep materials, and buffer time — handled autonomously. Your assistant learns your preferences and enforces them diplomatically.', icon: FiIcons.FiCalendar, tag: 'Day 1–3', accent: '#3D5C7A', accentLight: '#EBF0F5', accentBorder: '#B8CCDA', note: 'deep work blocks: protected' },
  { title: 'Walk in prepared every time', description: 'Before every meeting: attendee context, recent communications, agenda items, and suggested talking points. Lead with confidence.', icon: FiIcons.FiUsers, tag: 'Day 3–5', accent: '#2A6B4A', accentLight: '#EBF5F0', accentBorder: '#8ACAAA', note: 'briefs sent 15 min before' },
  { title: 'Communication that sounds like you', description: 'Through feedback and iteration, your assistant develops a sense of how you write and what you prioritise. By Week One, drafts need minimal editing — or none at all.', icon: FiIcons.FiMessageSquare, tag: 'Day 5–7', accent: '#5C4A2A', accentLight: '#F5F0E8', accentBorder: '#CAAA8A', note: '~95% approval rate by day 7' },
];

const processSteps = [
  { step: 'Kickoff Call', time: 'Day 0' },
  { step: 'Live Deployment', time: 'Day 1' },
  { step: 'Priority Mapping', time: 'Day 2–3' },
  { step: 'Pattern Absorption', time: 'Day 4–5' },
  { step: 'Fully Operational', time: 'Day 7' },
];

const FeatureCard = ({ feature, index }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
    transition={{ duration: 0.5, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
    className="card-premium rounded-2xl p-7 flex flex-col group relative overflow-hidden">
    <div className="absolute top-0 right-0 w-28 h-28 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-br-2xl"
      style={{ background: `radial-gradient(circle at top right, ${feature.accentLight}, transparent 70%)` }} />
    <div className="font-mono-custom text-[9px] tracking-[0.25em] mb-4 font-semibold uppercase" style={{ color: feature.accent }}>
      {feature.tag}
    </div>
    <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5 flex-shrink-0"
      style={{ background: feature.accentLight, border: `1px solid ${feature.accentBorder}` }}>
      <SafeIcon icon={feature.icon} style={{ color: feature.accent }} className="text-lg" />
    </div>
    <h3 className="text-base font-semibold text-[#1A1A18] mb-3 leading-snug">{feature.title}</h3>
    <p className="text-[#6B6560] leading-relaxed text-sm flex-grow mb-4">{feature.description}</p>
    {/* Handwritten annotation */}
    <div className="pt-3 border-t border-[#EDE9E4]">
      <p className="font-handwritten text-sm" style={{ color: feature.accent, transform: 'rotate(-0.5deg)' }}>
        ✎ {feature.note}
      </p>
    </div>
    <div className="absolute bottom-0 left-0 h-px w-0 group-hover:w-full transition-all duration-500"
      style={{ background: `linear-gradient(to right, ${feature.accent}30, transparent)` }} />
  </motion.div>
);

const Features = () => (
  <section id="capabilities" className="py-32 bg-white relative">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <div className="tag-pill mx-auto mb-5 w-fit">The Week One Journey</div>
        <h2 className="text-3xl md:text-5xl font-extrabold text-[#1A1A18] mb-3 leading-tight tracking-tight" style={{ letterSpacing: '-0.02em' }}>
          From setup to <span className="gradient-text-accent">indispensable</span> in one week.
        </h2>
        <ScribbleUnderline color="rgba(42,92,138,0.25)" width={260} className="mx-auto mb-5" />
        <p className="text-lg text-[#6B6560] leading-relaxed">
          Your assistant doesn't just arrive configured — it arrives learning. Every interaction refines its understanding.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
        {features.map((f, i) => <FeatureCard key={i} feature={f} index={i} />)}
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
        className="mt-12 px-8 py-5 rounded-2xl bg-[#F7F5F2] border border-[#E4DFD8] flex flex-wrap justify-around gap-5">
        {processSteps.map((item, i) => (
          <div key={item.step} className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-full bg-white border border-[#E4DFD8] flex items-center justify-center flex-shrink-0 shadow-sm">
              <span className="text-[10px] font-bold text-[#6B6560]">{i + 1}</span>
            </div>
            <div>
              <span className="text-sm font-semibold text-[#1A1A18] whitespace-nowrap block">{item.step}</span>
              <span className="text-[10px] font-mono-custom text-[#9E9690]">{item.time}</span>
            </div>
            {i < processSteps.length - 1 && <span className="text-[#C8C0B4] hidden sm:block ml-2">→</span>}
          </div>
        ))}
      </motion.div>
    </div>
  </section>
);

export default Features;